import { logger } from '@/utils/logger';
import {
  LEVEL_GROUPS,
  getSystemForLevel,
} from '@tutor/types';
import { EDUCATION_LEVELS_HIGHEST } from '@/constants/dropdownOptions';
import type { TutorRegistrationFormData, MultiLevelSubjectCard, ExamResult, SubjectCard } from '@/types';

export const defaultTeachingStyle = { iAm: [], iCan: [], iKnow: [], others: '' };

export const createEmptySubjectCard = (): MultiLevelSubjectCard => ({
  id: Date.now().toString(),
  educationSystem: '',
  educationLevels: [],
  subjects: [],
});

export const createEmptyExamResult = (): ExamResult => ({
  id: Date.now().toString(),
  examType: '',
  year: new Date().getFullYear(),
  results: [],
});

export const normalizeExamResults = (examResults: ExamResult[] = []): ExamResult[] =>
  examResults.map((exam, index) => ({
    ...exam,
    id: exam.id || `${Date.now()}-${index}`,
    results: Array.isArray(exam.results) ? exam.results : [],
  }));


// Convert ISO date string to YYYY-MM-DD format for HTML date input
export const formatDateToInput = (dateString: string | undefined | null): string => {
  if (!dateString) return '';

  logger.log('mapTutorProfileToFormData', 'formatDateToInput Called with', { dateString });

  // If it's already in YYYY-MM-DD format, return as-is
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    logger.log('mapTutorProfileToFormData', 'formatDateToInput Date already in YYYY-MM-DD format');
    return dateString;
  }

  // If it's ISO format (2026-04-13T00:00:00.000Z), extract the date part
  if (dateString.includes('T')) {
    const formatted = dateString.split('T')[0];
    logger.log('mapTutorProfileToFormData', 'formatDateToInput Converted ISO to YYYY-MM-DD', {
      original: dateString,
      formatted,
    });
    return formatted;
  }

  logger.warn('mapTutorProfileToFormData', 'formatDateToInput Unknown dateOfBirth format', { dateString });
  return '';
};

export type ProfileLike = Partial<TutorRegistrationFormData> & {
  currentStatus?: string;
  school?: string;
  teachingInstitution?: string;
  major?: string;
  availability?: Record<string, string[]>;
  teachingStyle?: typeof defaultTeachingStyle;
  teachingStyles?: typeof defaultTeachingStyle;
  subjects?: (SubjectCard | MultiLevelSubjectCard)[];
  subjectCards?: (SubjectCard | MultiLevelSubjectCard)[];
  examResults?: ExamResult[];
  hourlyRate?: number;
  yearsOfExperience?: number | string;
  yearsOfTuitionExperience?: number | string;
  fullName?: string;
  dateOfBirth?: string;
  gender?: string | null;
  nationality?: string;
  phone?: string;
  tuitionType?: string;
  preferredState?: string;
  preferredDistricts?: string[];
  preferredAreaNotes?: string[];
  maxTravelDistance?: string;
  highestEducationLevel?: string;
  university?: string;
  fieldOfStudy?: string;
  yearOfGraduation?: number;
  weeklySchedule?: Record<string, string[]>;
  teachingLanguages?: string[];
  customLanguage?: string;
  tutorExperienceTypes?: string[];
  aboutMe?: string;
  agreedToTerms?: boolean;
};

export const mapProfileToFormData = (
  profile: ProfileLike,
  prev: TutorRegistrationFormData
): TutorRegistrationFormData => {
  const subjectSource = profile.subjects || profile.subjectCards || [];
  const examSource = profile.examResults || [];

  const isCustomEducation = profile.highestEducationLevel &&
    !EDUCATION_LEVELS_HIGHEST.some(level => level.value === profile.highestEducationLevel);

  // Group API SubjectCards by level group + subjects fingerprint into MultiLevelSubjectCards
  const convertedSubjectCards = (() => {
    if (!subjectSource.length) return [createEmptySubjectCard()];

    type SubjectSourceCard = SubjectCard | MultiLevelSubjectCard;

    // Normalize each card to { educationLevel: EducationLevel, subjects: string[] }
    const normalized = subjectSource.map((card: SubjectSourceCard) => ({
      educationSystem: 'educationSystem' in card ? card.educationSystem : null,
      educationLevel: 'educationLevel' in card ? card.educationLevel : null,
      educationLevels: 'educationLevels' in card && Array.isArray(card.educationLevels) ? card.educationLevels : null,
      subjects: Array.isArray(card.subjects) ? card.subjects : [] as string[],
    }));

    // Find which LEVEL_GROUP a level belongs to
    const getGroupKey = (level: string): string => {
      const entry = Object.entries(LEVEL_GROUPS).find(([, levels]) =>
        levels.some((candidateLevel) => candidateLevel === level)
      );
      return entry ? entry[0] : level;
    };

    // Merge cards: group by (groupKey + sorted subjects fingerprint)
    const mergeMap = new Map<string, { educationSystem: string; educationLevels: string[]; subjects: string[] }>();

    for (const card of normalized) {
      const levels: string[] = card.educationLevels ?? (card.educationLevel ? [card.educationLevel] : []);
      if (!levels.length) continue;

      const groupKey = getGroupKey(levels[0]);
      const subjectsKey = [...card.subjects].sort().join('|');
      const educationSystem = card.educationSystem ?? getSystemForLevel(levels[0]) ?? '';
      const mapKey = `${educationSystem}::${groupKey}::${subjectsKey}`;

      if (mergeMap.has(mapKey)) {
        const existing = mergeMap.get(mapKey)!;
        for (const l of levels) {
          if (!existing.educationLevels.includes(l)) existing.educationLevels.push(l);
        }
      } else {
        mergeMap.set(mapKey, { educationSystem, educationLevels: [...levels], subjects: card.subjects });
      }
    }

    return Array.from(mergeMap.values()).map((entry, index) => ({
      id: `${Date.now()}-${index}`,
      educationSystem: entry.educationSystem,
      educationLevels: entry.educationLevels,
      subjects: entry.subjects,
    }));
  })();

  return {
    ...prev,
    fullName: profile.fullName || '',
    dateOfBirth: formatDateToInput(profile.dateOfBirth),
    gender: profile.gender ?? '',
    nationality: profile.nationality || '',
    phone: profile.phone || '',
    tuitionType: profile.tuitionType || '',
    preferredState: profile.preferredState || '',
    preferredDistricts: profile.preferredDistricts || [],
    preferredAreaNotes: profile.preferredAreaNotes || [],
    maxTravelDistance: profile.maxTravelDistance || '',
    currentStatus: profile.currentStatus || '',
    highestEducationLevel: isCustomEducation ? 'others' : (profile.highestEducationLevel || ''),
    otherEducationLevel: isCustomEducation ? (profile.highestEducationLevel || '') : '',
    school: profile.university || profile.school || '',
    teachingInstitution: profile.teachingInstitution || '',
    major: profile.fieldOfStudy || profile.major || '',
    availability: profile.weeklySchedule || profile.availability || {},
    teachingLanguages: profile.teachingLanguages || [],
    customLanguage: profile.customLanguage || '',
    hourlyRate: profile.hourlyRate ? String(profile.hourlyRate) : '',
    yearsOfTuitionExperience: profile.yearsOfTuitionExperience ?? profile.yearsOfExperience ?? '',
    tutorExperienceTypes: profile.tutorExperienceTypes || [],
    examResults: examSource.length > 0 ? normalizeExamResults(examSource) : [],
    teachingStyle: profile.teachingStyle || profile.teachingStyles || defaultTeachingStyle,
    aboutMe: profile.aboutMe || '',
    agreedToTerms: Boolean(profile.agreedToTerms),
    subjectCards: convertedSubjectCards as MultiLevelSubjectCard[],
  };
};
